import { PostgrestSingleResponse, SupabaseClient } from '@supabase/supabase-js';
import { Observable, Subject, lastValueFrom } from 'rxjs';
import { v4 } from 'uuid';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { Base } from '../../models/base.model';

import { ICrud } from './crud.interface';
import { Database } from '../../models/types';
import { SupabaseService } from './supabase.service';
import { inject } from '@angular/core';
import { TextFilterConfig } from 'src/app/shared/components/smart/table/table.component';

/**
 * Views that can no longer be read through PostgREST: they were created without
 * `security_invoker = on`, so they ran as their owner and bypassed RLS on the
 * cp_* tables underneath. Turning the flag on statement-timeouts at prod scale,
 * so each is served by a lambda running as service_role behind the role check
 * its base tables' RLS policies describe, and the `authenticated` grant is
 * revoked.
 *
 * The mapping lives here rather than at each call site so that every consumer
 * that goes through load() — smart table, big-number tiles, autocomplete — is
 * routed automatically. A call site missed by hand would only fail once the
 * grant is revoked, i.e. in production.
 */
export const VIEW_LAMBDA_ENDPOINTS: Record<string, string> = {
  cpv_user_kyc_v2: 'user_kyc_list',
  x_clients_overview_v1: 'clients_overview_list',
};

export class DatabaseService<T extends Base> implements ICrud<T> {
  protected table: string;
  // private supabaseUrl: string;
  // private supabaseKey: string;
  private sbs: SupabaseService = inject(SupabaseService);
  // Http client used for calling lambda endpoints when configured
  protected httpIntern: HttpClient = inject(HttpClient);
  public supabase: SupabaseClient<Database>;

  /**
   * Optional configuration that subclasses can set to route write
   * operations through lambdas instead of using the Supabase client
   * directly.
   * Example: in CommunicationService set
   * this.lambdaNames = { create: 'create_thread', update: 'update_thread' }
   */
  protected lambdaNames?: { create?: string; update?: string; delete?: string };

  /**
   * When set, list() calls this lambda endpoint (POST) instead of querying
   * Supabase directly — for entities whose table/view can't be exposed via
   * direct PostgREST grant (e.g. a materialized view with no RLS of its own).
   * The lambda is expected to accept { pageIndex, pageSize, filters, sort,
   * search, countExact } and return { data, count } (optionally nested under
   * `details`, matching retSuccess()'s response shape).
   */
  protected lambdaListName?: string;

  public _getPagination = (page: number, size: number) => {
    const limit = size ? +size : 3;
    const from = page ? page * limit : 0;
    const to = page ? from + size - 1 : size - 1;
    return { from, to };
  };

  constructor(table: string) {
    this.table = table;
    this.supabase = this.sbs.supabase;
  }

  private handleError(error: any) {
    if (error) {
      throw new Error(error.message);
    }
  }

  async get(id: string): Promise<T> {
    const { data, error } = await this.supabase
      .from(this.table)
      .select()
      .eq('id', id)
      .single();

    this.handleError(error);

    return data as T;
  }

  async getAll(limit: number, page: number): Promise<T[]> {
    const query = this.supabase.from(this.table).select();
    if (limit) query.limit(limit);
    if (page) {
      const { from, to } = this._getPagination(page, limit);
      query.range(from, to);
    }

    const { data, error } = await query;

    this.handleError(error);

    return (data as T[]) ?? [];
  }

  // async getAllDistinct(column: string): Promise<any[]> {
  //   const { data, error } = await this.supabase
  //     .rpc('distinct_values', { schema: 'public', table: this.table, column })
  //     .select('distinct_values');

  //   if (error) {
  //     throw new Error(error.message);
  //   }

  //   return data?.map((item: any) => item.distinct_values);
  // }

  async create(t: Partial<T>) {
    // If a lambda is configured for create operations, call it instead
    if (this.lambdaNames && this.lambdaNames.create) {
      const res = await this.callLambda(this.lambdaNames.create, 'POST', t);
      return res?.data ?? res;
    }

    const { data, error } = await this.supabase
      .from(this.table)
      .insert(t)
      .select();

    this.handleError(error);

    return data;
  }

  async update(t: Partial<T>) {
    if (this.lambdaNames && this.lambdaNames.update) {
      const res = await this.callLambda(this.lambdaNames.update, 'PUT', {
        id: t.id,
        patch: t,
      });
      return res?.data ?? res;
    }

    const { data, error } = await this.supabase
      .from(this.table)
      .update(t)
      .eq('id', t.id)
      .select();

    if (error) console.error('UPDATE ERROR', error);
    this.handleError(error);

    return data;
  }

  async delete(t: T) {
    console.log('DELETE', t);
    console.log('DELETE', this.table);
    if (this.lambdaNames && this.lambdaNames.delete) {
      const res = await this.callLambda(
        this.lambdaNames.delete,
        'DELETE',
        undefined,
        String(t.id)
      );
      return res?.data ?? res;
    }

    const { data, error } = await this.supabase
      .from(this.table)
      .delete()
      .eq('id', t.id);

    this.handleError(error);

    return data;
  }

  // Protected helper to call a lambda HTTP endpoint configured in serverless
  protected async callLambda(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: any,
    pathParam?: string
  ): Promise<any> {
    const base = (environment.lambdaUrl || '').replace(/\/$/, '');
    const url = pathParam
      ? `${base}/${endpoint}/${encodeURIComponent(pathParam)}`
      : `${base}/${endpoint}`;

    // try to obtain the current user's access token from the supabase client
    let token: string | null = null;
    try {
      // supabase-js v2
      // @ts-ignore
      const sess = await this.supabase.auth.getSession();
      token = sess?.data?.session?.access_token ?? null;
    } catch (e) {
      // ignore and proceed without token
      token = null;
    }

    const headersObj: any = { 'Content-Type': 'application/json' };
    if (environment.supabaseKey) headersObj['apikey'] = environment.supabaseKey;
    if (token) headersObj['Authorization'] = `Bearer ${token}`;

    const headers = new HttpHeaders(headersObj);

    try {
      const obs = this.httpIntern.request(method, url, { body, headers });
      const resp = await lastValueFrom(obs as any);
      return resp;
    } catch (err: any) {
      console.error('Lambda call error', endpoint, err);
      throw err;
    }
  }

  list(
    pageIndex: number = 0,
    pageSize: number = 5,
    filters: Filter[] = [],
    sort: Sort[] = [],
    search?: TextFilterConfig,
    countExact = false
  ): Observable<QueryResult<T>> {
    const subj = new Subject<QueryResult<T>>();

    if (this.lambdaListName) {
      this.callLambda(this.lambdaListName, 'POST', {
        pageIndex,
        pageSize,
        filters,
        sort,
        search,
        countExact,
      })
        .then((res: any) => {
          const body = res?.details ?? res ?? {};
          subj.next({ data: body.data ?? [], count: body.count ?? 0 });
        })
        .catch((err) => {
          console.error('Lambda list error', this.lambdaListName, err);
          subj.next({ data: [], count: 0 });
        });
      return subj.asObservable();
    }

    // If there's a `contains` filter on the id column, we cannot call ILIKE on uuid
    // via the REST filters. Instead call an RPC that searches UUIDs as text and
    // convert the result into an `in` or `eq` filter. Keep everything async via
    // promise chains so this method remains synchronous (returns Observable).

    // detect filters on id that use text-matching operators which cannot be
    // applied directly to uuid columns via REST (ilike). We handle these by
    // resolving matching UUIDs via an RPC and converting to eq/in filters.
    const idContainsIndex = filters.findIndex(
      (f) =>
        f.column === 'id' &&
        (f.operator === 'contains' ||
          f.operator === 'startswith' ||
          f.operator === 'endswith')
    );

    // Use object-oriented helpers instead of an inline function
    const run = (effectiveFilters: Filter[], pageIdx: number, pageSz: number) =>
      this.runQueryAndEmit(
        effectiveFilters,
        pageIdx,
        pageSz,
        sort,
        search,
        subj,
        countExact
      );

    if (idContainsIndex === -1) {
      // No special id contains filter: build and run normally
      run(filters, pageIndex, pageSize);
    } else {
      // Extract the id filter and resolve matching UUIDs, then run
      const idFilter = filters[idContainsIndex];

      this.resolveUuidFilters(idFilter, filters, idContainsIndex)
        .then((newFilters) => run(newFilters, pageIndex, pageSize))
        .catch((err) => {
          console.error('Error resolving uuid filters:', err);
          // on error, force empty result set
          const newFilters = filters.slice();
          newFilters.splice(idContainsIndex, 1);
          newFilters.push({
            column: 'id',
            operator: 'eq',
            value: '00000000-0000-0000-0000-000000000000',
            visible: false,
          });
          run(newFilters, pageIndex, pageSize);
        });
    }
    return subj.asObservable();
  }

  // Build the supabase query applying filters, pagination, sorting and search
  private buildQuery(
    effectiveFilters: Filter[],
    pageIndex: number,
    pageSize: number,
    sort: Sort[],
    search?: TextFilterConfig,
    countExact = false
  ) {
    let q: any = this.supabase
      .from(this.table)
      .select('*', { count: countExact ? 'exact' : 'estimated' });

    for (const filter of effectiveFilters) {
      q = applyFilter(q, filter);
    }

    q = q.range(pageIndex * pageSize, (pageIndex + 1) * pageSize - 1);

    for (const s of sort) {
      q = q.order(s.column, { ascending: s.direction === 'asc' });
    }

    if (search) {
      q = q.textSearch(search.column, search.value, search.options);
    }

    return q;
  }

  // Run a built query and emit result to the provided Subject
  private runQueryAndEmit(
    effectiveFilters: Filter[],
    pageIndex: number,
    pageSize: number,
    sort: Sort[],
    search: TextFilterConfig | undefined,
    subj: Subject<QueryResult<T>>,
    countExact = false
  ) {
    const q = this.buildQuery(
      effectiveFilters,
      pageIndex,
      pageSize,
      sort,
      search,
      countExact
    );
    q.then((ev: any) => {
      this.handleError(ev.error);
      subj.next({ data: ev.data as T[], count: ev.count ?? 0 });
    });
  }

  // Resolve filters that target uuid columns (id) using the RPC and optional
  // client-side refinements. Returns a new filters array where the original id
  // text-matching filter is replaced by an eq/in (or an always-empty eq) filter.
  private async resolveUuidFilters(
    idFilter: Filter,
    allFilters: Filter[],
    idIndex: number
  ): Promise<Filter[]> {
    const newFilters = allFilters.slice();
    // remove the original id filter
    newFilters.splice(idIndex, 1);

    try {
      const res: any = await (this.supabase as any).rpc('search_uuid_text', {
        search_term: idFilter.value,
        table_name: this.table,
      });

      if (res?.error) {
        console.error('RPC search_uuid_text error:', res.error);
        newFilters.push({
          column: 'id',
          operator: 'eq',
          value: '00000000-0000-0000-0000-000000000000',
          visible: false,
        });
        return newFilters;
      }

      const rows: any[] = Array.isArray(res?.data) ? res.data : [];
      let filteredRows = rows;

      // RPC always does ILIKE '%term%'. If caller wanted startswith/endswith,
      // refine client-side.
      if (idFilter.operator === 'startswith') {
        filteredRows = rows.filter((r: any) =>
          String(r.uuid_result).startsWith(String(idFilter.value))
        );
      } else if (idFilter.operator === 'endswith') {
        filteredRows = rows.filter((r: any) =>
          String(r.uuid_result).endsWith(String(idFilter.value))
        );
      }

      const ids: string[] = filteredRows
        .map((r: any) => r.uuid_result)
        .filter(Boolean);

      if (ids.length === 0) {
        newFilters.push({
          column: 'id',
          operator: 'eq',
          value: '00000000-0000-0000-0000-000000000000',
          visible: false,
        });
      } else if (ids.length === 1) {
        newFilters.push({
          column: 'id',
          operator: 'eq',
          value: ids[0],
          visible: false,
        });
      } else {
        newFilters.push({
          column: 'id',
          operator: 'in',
          value: ids,
          visible: false,
        });
      }

      return newFilters;
    } catch (err) {
      console.error('Error mapping RPC result to ids:', err);
      newFilters.push({
        column: 'id',
        operator: 'eq',
        value: '00000000-0000-0000-0000-000000000000',
        visible: false,
      });
      return newFilters;
    }
  }

  /**
   * `opts.limitOne` (CPK-4911): sends the HEAD with `limit=1`. PostgREST computes the
   * total in a separate `SELECT 1 …` CTE, but also runs `count(_postgrest_t)` over the
   * whole row of the unlimited result — on a heavy view that computes every column of
   * every row. With `limit=1` the total stays exact and only one row is materialized.
   */
  count(
    filters: Filter[] = [],
    search?: TextFilterConfig,
    opts?: { limitOne?: boolean }
  ): Observable<QueryResult<T>> {
    const subj = new Subject<QueryResult<T>>();

    if (this.lambdaListName) {
      this.callLambda(this.lambdaListName, 'POST', {
        filters,
        search,
        countExact: true,
        headOnly: true,
      })
        .then((res: any) => {
          const body = res?.details ?? res ?? {};
          subj.next({ data: [], count: body.count ?? 0 });
        })
        .catch((err) => {
          console.error('Lambda count error', this.lambdaListName, err);
          subj.next({ data: [], count: 0 });
        });
      return subj.asObservable();
    }

    let a = this.supabase
      .from(this.table)
      .select('*', { count: 'exact', head: true });

    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i];
      a = applyFilter(a, filter);
    }

    if (search && search.value) {
      a = a.textSearch(search.column, search.value, search.options);
    }

    if (opts?.limitOne) {
      a = a.limit(1);
    }

    a.then((ev) => {
      if (ev.error) {
        console.error('DatabaseService.count error', this.table, ev.error);
        subj.next({ data: [], count: 0 });
        return;
      }
      subj.next({ data: ev.data as T[], count: ev.count ?? 0 });
    });

    return subj.asObservable();
  }

  async getAllDistinct(column: string) {
    const data = await this.supabase
      .from(this.table)
      .select(`${column}.count()`, { count: 'exact' });

    this.handleError(data.error);

    return data;
  }
}

export interface QueryResult<T> {
  data: T[];
  count: number;
}
export interface Sort {
  column: string;
  direction: 'asc' | 'desc';
}
export interface Filter {
  column: string;
  operator:
    | 'gte'
    | 'gt'
    | 'eq'
    | 'lt'
    | 'lte'
    | 'ilike'
    | 'neq'
    | 'contains'
    | 'ncontains'
    | 'empty'
    | 'nempty'
    | 'in'
    | 'startswith'
    | 'endswith'
    | 'or'
    | 'jsonbcontains'
    | 'jsonbnotcontains';
  value: string | number | boolean | any[] | null | Record<string, any>;
  visible: boolean;
}

function applyFilter(current: any, filter: Filter): any {
  if (filter.value === null || filter.value === undefined) return current;

  try {
    switch (filter.operator) {
      case 'contains':
        return current.ilike(filter.column, `%${filter.value}%`);
      case 'ncontains':
        return current.not(filter.column, 'ilike', `%${filter.value}%`);
      case 'startswith':
        return current.ilike(filter.column, `${filter.value}%`);
      case 'endswith':
        return current.ilike(filter.column, `%${filter.value}`);
      case 'ilike':
        return current.ilike(filter.column, String(filter.value));
      case 'empty':
        return current.is(filter.column, null);
      case 'nempty':
        return current.not(filter.column, 'is', null);
      case 'eq':
        return current.eq(filter.column, filter.value);
      case 'neq':
        return current.neq(filter.column, filter.value);
      case 'gte':
        return current.gte(filter.column, filter.value);
      case 'lte':
        return current.lte(filter.column, filter.value);
      case 'gt':
        return current.gt(filter.column, filter.value);
      case 'lt':
        return current.lt(filter.column, filter.value);
      case 'in':
        if (Array.isArray(filter.value) && filter.value.length > 0) {
          return current.in(filter.column, filter.value as any[]);
        }
        // empty array -> no-op (don't apply filter)
        return current;
      case 'or':
        return current.or(filter.value);
      case 'jsonbcontains':
        return current.contains(filter.column, filter.value as Record<string, any>);
      case 'jsonbnotcontains':
        return current.not(filter.column, 'cs', filter.value);
      default:
        // fallback: try to call the operator dynamically if available
        if (typeof current[filter.operator] === 'function') {
          return current[filter.operator](filter.column, filter.value);
        }
        console.warn('Unsupported filter operator', filter.operator);
        return current;
    }
  } catch (e) {
    console.warn('applyFilter error', e);
    return current;
  }
}
